import { Chip } from '@mui/material';
import _ from 'lodash';
import React from 'react';
import { connectCurrentRefinements } from 'react-instantsearch-dom';
import styled from '@emotion/styled';

import { BREAKPOINTS, COLORS, FONT_WEIGHT, SPACE, BODY_STYLES } from '@/constants';
import { convertToCurrencyFormat } from '@/utils/convert-to-currency-format';
import { ExtraSmallStyles, SmallStyles } from '@/components/type';
import { priceRangeSliderMarks } from '@/components/discovery-questions-input/utils/price-range-slider-marks';

import { yearSliderMarks, POWER_TYPE_MAPPING } from './constants';
import { algoliaAttributes } from './utils';
import { getIsDefaultFilterState } from '@/utils/get-is-default-filter-state';

const ClearLink = styled.div`
  ${SmallStyles};
  cursor: pointer;
  text-decoration: underline;
  user-select: none;
  min-width: 100px;
  margin-top: ${SPACE.X0};
  margin-bottom: calc(${SPACE.X1} + ${SPACE.X0});

  :focus-visible {
    color: ${COLORS.BRAND_BLUE_EIGHT};
  }

  :focus:not(:focus-visible) {
    outline: none;
  }
`;

const ChipsPanel = styled.div`
  display: flex;
  flex-direction: column;
  @media (min-width: ${BREAKPOINTS.MD}px) {
    justify-content: space-between;
    align-items: center;
    flex-direction: row;
  }
`;

const ChipsWrapper = styled.ul`
  display: flex;
  list-style-type: none;
  padding: ${SPACE.X0} 0;
  flex-wrap: wrap;
`;

const StyledChip = styled(Chip)`
  &.MuiChip-colorPrimary {
    background-color: ${COLORS.BRAND_BLUE_EIGHT};
    ${ExtraSmallStyles};
    font-weight: ${FONT_WEIGHT.BOLD};
    margin-right: ${SPACE.X1};
    margin-bottom: ${SPACE.X0};
    margin-top: ${SPACE.X0};
    border-radius: 0;
    user-select: none;

    &:hover {
      background-color: ${COLORS.BRAND_BLUE_THREE};
      color: ${COLORS.GREY_TEN};
    }
    &:focus-visible {
      background-color: ${COLORS.BRAND_BLUE_THREE};
      color: ${COLORS.GREY_TEN};
    }

    &:focus:not(:focus-visible) {
      outline: none;
      background-color: ${COLORS.BRAND_BLUE_THREE};
      color: ${COLORS.GREY_TEN};
    }
  }
`;

const StyledChipLabel = styled.span`
  position: absolute !important;
  clip: rect(1px, 1px, 1px, 1px);
  width: 1px !important;
  height: 1px !important;
  padding: 0 !important;
  border: 0 !important;
  overflow: hidden;
  white-space: nowrap;
`;

// @ts-expect-error [🤖 PLEASE FIX 🤖]: Parameter 'label' implicitly has an 'any' type.
const buildChip = (label, onDelete, key) => (
  <li>
    <StyledChip
      key={key}
      {...{
        label: (
          <div>
            {label}
            <StyledChipLabel>{` Press delete to remove the ${label} filter`}</StyledChipLabel>
          </div>
        ),
        onDelete,
        color: 'primary',
        onKeyDown: (event) => {
          if (event.key === ' ') {
            event.preventDefault();
            onDelete();
          }
        },
        onClick: onDelete,
      }}
    />
  </li>
);

// @ts-expect-error [🤖 PLEASE FIX 🤖]: Parameter 'selectedRange' implicitly has an 'any' type.
const buildPriceFilterChips = (selectedRange, resetSelectedPriceRange) => {
  const isSliderFilter = _.isArray(selectedRange);
  if (!isSliderFilter) {
    return undefined;
  }

  // @ts-expect-error [🤖 PLEASE FIX 🤖]: Parameter 'value' implicitly has an 'any' type.
  const formatter = (value) => {
    // @ts-expect-error [🤖 PLEASE FIX 🤖]: Object is possibly 'undefined'.
    const suffix = value === _.last(priceRangeSliderMarks).value ? '+' : '';
    return `$${convertToCurrencyFormat(value)}${suffix}`;
  };

  // @ts-expect-error [🤖 PLEASE FIX 🤖]: Object is possibly 'undefined'.
  const defaultMarks = [_.first(priceRangeSliderMarks).value, _.last(priceRangeSliderMarks).value];
  const isDefault = selectedRange[0] === defaultMarks[0] && selectedRange[1] === defaultMarks[1];
  if (isDefault) {
    return undefined;
  }

  const onDelete = async () => {
    resetSelectedPriceRange();
  };
  return buildChip(`${formatter(selectedRange[0])} - ${formatter(selectedRange[1])}`, onDelete, 'price');
};

// @ts-expect-error [🤖 PLEASE FIX 🤖]: Binding element 'items' implicitly has an 'any' type.
const AlgoliaChips = ({ items, onRemove }) => {
  // @ts-expect-error [🤖 PLEASE FIX 🤖]: 'item' is of type 'unknown'.
  const uniqueItems = _.uniqBy(items, (item) => item.label);
  const uniqueItemsWithoutDefaultYear = _.filter(
    uniqueItems,
    // @ts-expect-error [🤖 PLEASE FIX 🤖]: Object is possibly 'undefined'.
    ({ label }) => label !== `${_.first(yearSliderMarks).value} - ${_.last(yearSliderMarks).value}`
  );

  // @ts-expect-error [🤖 PLEASE FIX 🤖]: Parameter 'item' implicitly has an 'any' type.
  const onDelete = (item) => {
    if (item.id === 'year') {
      onRemove();
    } else {
      onRemove(item.id);
    }
  };

  return _.map(uniqueItemsWithoutDefaultYear, (item) => {
    // @ts-expect-error [🤖 PLEASE FIX 🤖]: 'item' is of type 'unknown'.
    const hasSubItems = !_.isEmpty(item.items);

    if (!hasSubItems) {
      // @ts-expect-error [🤖 PLEASE FIX 🤖]: 'item' is of type 'unknown'.
      return buildChip(item.label, () => onDelete(item), item.label);
    }

    // @ts-expect-error [🤖 PLEASE FIX 🤖]: 'item' is of type 'unknown'.
    const uniqueSubItems = _.uniqBy(item.items, (nested) => nested.label);
    // @ts-expect-error [🤖 PLEASE FIX 🤖]: 'nested' is of type 'unknown'.
    return _.map(uniqueSubItems, (nested) => buildChip(nested.label, () => onDelete(nested), nested.label));
  });
};

// @ts-expect-error [🤖 PLEASE FIX 🤖]: Binding element 'reset' implicitly has an 'any' type.
const ClearAllLink = ({ reset, items, displayClearLink, priceFilterChip, isDefaultFilterState }) => {
  if (!displayClearLink || isDefaultFilterState) {
    return null;
  }

  const onClearLinkClick = () => {
    reset();
  };

  return (
    (!_.isEmpty(items) || !_.isEmpty(priceFilterChip)) && (
      <ClearLink
        tabIndex={0}
        onClick={onClearLinkClick}
        onKeyDown={(event) => {
          if (event.key === ' ') {
            event.preventDefault();
            onClearLinkClick();
          }
        }}
        role="button"
      >
        Clear all filters
      </ClearLink>
    )
  );
};

// @ts-expect-error [🤖 PLEASE FIX 🤖]: Parameter 'items' implicitly has an 'any' type.
const transformLabelsToHumanReadable = (items, safetyFeatures) =>
  _.map(items, (item) => {
    let { label } = item;
    let innerItems = item.items;

    if (item.attribute === algoliaAttributes.POWER_TYPE) {
      // Group the expanded values back to their aggregated display value
      // eg. [Mild Hybrid, Hybrid, Plug-in Hybrid] -> Hybrid
      const groupedItems = _.uniqBy(innerItems, (nested) => {
        return (
          // @ts-expect-error [🤖 PLEASE FIX 🤖]: 'nested' is of type 'unknown'.
          Object.entries(POWER_TYPE_MAPPING).find(([, values]) => values.includes(nested.label))?.[0] || nested.label
        );
      });

      innerItems = groupedItems.map((nested) => {
        const displayValue =
          // @ts-expect-error [🤖 PLEASE FIX 🤖]: 'nested' is of type 'unknown'.
          Object.entries(POWER_TYPE_MAPPING).find(([, values]) => values.includes(nested.label))?.[0] ||
          // @ts-expect-error [🤖 PLEASE FIX 🤖]: 'nested' is of type 'unknown'.
          nested.label;

        return {
          // @ts-expect-error [🤖 PLEASE FIX 🤖]: Spread types may only be created from object types.
          ...nested,
          id: displayValue,
          label: displayValue,
        };
      });
    } else if (item.attribute === algoliaAttributes.YEAR) {
      label = `${item.currentRefinement.min} - ${item.currentRefinement.max}`;
    } else {
      innerItems = _.map(innerItems, (nested) => {
        let innerLabel = nested.label;

        const selectedBodyStyle = _.find(BODY_STYLES, (bodyStyle, index) => nested.label === index);
        if (selectedBodyStyle) {
          innerLabel = selectedBodyStyle.label;
        }

        if (item.attribute === algoliaAttributes.MAKE) {
          innerLabel = _.startCase(nested.label);
        } else if (item.attribute === algoliaAttributes.SAFETY_FEATURES) {
          // if safety features, we need to use the label attribute of the passed in safetyFeatures array,
          // since the items that algolia passes in doesnt include this data.
          innerLabel = _.get(
            _.find(safetyFeatures, (safetyFeature) => safetyFeature.id === nested.label),
            'label'
          );
        }

        return { ...nested, id: nested.label, label: innerLabel };
      });
    }

    return { ...item, items: innerItems, label };
  });

// @ts-expect-error [🤖 PLEASE FIX 🤖]: Parameter 'id' implicitly has an 'any' type.
const filterItems = (id, items) => {
  // @ts-expect-error [🤖 PLEASE FIX 🤖]: Element implicitly has an 'any' type because expression of type 'any' can't be used to index type '{ MAKE: string; BODY_STYLE: string; YEAR: string; STAR_RATING: string; SAFETY_FEATURES: string; POWER_TYPE: string; LIFESTYLE: string; }'.
  return _.filter(items, ({ attribute }) => attribute === algoliaAttributes[id]);
};

const ConnectedAlgoliaChips = connectCurrentRefinements(AlgoliaChips);
const ConnectedClearAllLink = connectCurrentRefinements(ClearAllLink);

const ChipsForFilters = ({
  // @ts-expect-error [🤖 PLEASE FIX 🤖]: Binding element 'uiSelectedPriceRange' implicitly has an 'any' type.
  uiSelectedPriceRange,
  // @ts-expect-error [🤖 PLEASE FIX 🤖]: Binding element 'reset' implicitly has an 'any' type.
  reset,
  // @ts-expect-error [🤖 PLEASE FIX 🤖]: Binding element 'resetSelectedPriceRange' implicitly has an 'any' type.
  resetSelectedPriceRange,
  // @ts-expect-error [🤖 PLEASE FIX 🤖]: Binding element 'filterConfig' implicitly has an 'any' type.
  filterConfig,
  // @ts-expect-error [🤖 PLEASE FIX 🤖]: Binding element 'safetyFeatures' implicitly has an 'any' type.
  safetyFeatures,
  // @ts-expect-error [🤖 PLEASE FIX 🤖]: Binding element 'displayClearLink' implicitly has an 'any' type.
  displayClearLink,
  // @ts-expect-error [🤖 PLEASE FIX 🤖]: Binding element 'filterState' implicitly has an 'any' type.
  filterState,
}) => {
  const isDefaultState = getIsDefaultFilterState(filterState);
  const priceFilterChip = buildPriceFilterChips(uiSelectedPriceRange, resetSelectedPriceRange);

  return (
    <ChipsPanel>
      <ChipsWrapper>
        {priceFilterChip}
        {_.map(filterConfig, ({ id, onRemove }) => {
          return (
            <ConnectedAlgoliaChips
              key={id}
              // @ts-expect-error [🤖 PLEASE FIX 🤖]: Type '{ key: any; displayClearLink: any; transformItems: (items: any) => any[]; onRemove: any; }' is not assignable to type 'IntrinsicAttributes & IntrinsicClassAttributes<Component<Omit<{ items: any; onRemove: any; }, CurrentRefinementsProvided> & CurrentRefinementsExposed, any, any>> & Readonly<...>'.
              displayClearLink={displayClearLink}
              transformItems={(items) => transformLabelsToHumanReadable(filterItems(id, items), safetyFeatures)}
              onRemove={onRemove}
            />
          );
        })}
      </ChipsWrapper>

      {displayClearLink && !isDefaultState && (
        <ClearLink onClick={reset} tabIndex={0} role="button">
          Clear all filters
        </ClearLink>
      )}
    </ChipsPanel>
  );
};

export default ChipsForFilters;
export { StyledChip, ClearLink, ConnectedAlgoliaChips, ConnectedClearAllLink };
